import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/modules/auth/useAuth';
import { useOrCreateConversation } from '@/modules/messages/useOrCreateConversation';
import { motion } from 'framer-motion';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { SignalPill } from '@/components/ui/SignalPill';
import { StatusDot } from '@/components/ui/StatusDot';
import {
  ActionBar,
  Button as KitButton,
  MetaGroup,
  MetaRow,
  MetaStack,
  SaveControl,
} from '@/kit';
import { GridTile } from '@/components/feed/GridTile';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import { ActionSheet, type ActionSheetItem } from '@/components/sheets/ActionSheet';
import { CloseProfileButton } from '@/components/ui/CloseProfileButton';
import { useProfiles, useProfileByHandle } from '@/modules/profiles/useProfiles';
import { capture } from '@/modules/heat/signals';
import { useBlocks } from '@/modules/trust/useBlocks';
import { ReportSheet } from '@/modules/trust/ReportSheet';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/cn';

const EASE = [0.19, 1, 0.22, 1] as const;

const BAND_LABEL: Record<string, string> = {
  pulse: 'pulse',
  hot: 'heat',
  warm: 'signal',
  cool: 'idle',
};

/**
 * Profile detail — full-viewport cinematic on desktop, editorial sheet on mobile.
 *
 * Desktop: hero scene fills the left two-thirds, sticky meta column on the right.
 * Mobile: 75dvh hero, scrollable meta below.
 */
export const ProfilePage = () => {
  const { handle = '' } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { profile } = useProfileByHandle(handle);
  const { profiles } = useProfiles();

  const { user } = useAuth();
  const { getOrCreate } = useOrCreateConversation();
  const [messaging, setMessaging] = useState(false);

  const handleMessage = async () => {
    if (!profile?.ownerId) return;
    setMessaging(true);
    const result = await getOrCreate(profile.ownerId);
    setMessaging(false);
    if (result.ok) navigate(ROUTES.messages);
  };

  // Profile-action sheet
  const [actionsOpen, setActionsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [favourited, setFavourited] = useState(false);
  const { block, unblock, isBlocked } = useBlocks();
  // Magazine-spread gallery: which photo is showing as the hero
  const [activePhoto, setActivePhoto] = useState(0);

  // Auto-clear toast
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (profile) capture('profile_view', 'profile', profile.id);
  }, [profile]);

  if (!profile) {
    return (
      <PageShell
        rail={false}
        mobileTopBar={
          <>
            <button
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="text-text-secondary"
            >
              <Icon name="arrow_back" size={20} />
            </button>
            {/* The wordmark that used to centre this bar now lives in
                PageShell's header as the WorldSwitcher — one lockup per
                screen. Back stays; it is the only control this state has. */}
          </>
        }
      >
        <div className="mx-auto max-w-md px-6 py-24 text-center">
          <SectionMarker className="mb-4 justify-center" muted>
            404
          </SectionMarker>
          <h2 className="font-display text-3xl font-black uppercase tracking-display">
            Not in the room<span className="text-pink">.</span>
          </h2>
          <p className="mt-3 text-sm text-text-secondary">
            No profile at @{handle}.
          </p>
          <Link to={ROUTES.feed} className="mt-7 inline-block">
            <NeonButton variant="outline" size="md">
              Back to feed
            </NeonButton>
          </Link>
        </div>
      </PageShell>
    );
  }

  const others = profiles
    .filter((p) => p.id !== profile.id && p.location === profile.location)
    .slice(0, 6);
  const elsewhere = profiles
    .filter((p) => p.id !== profile.id && p.location !== profile.location)
    .slice(0, 6);

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="text-text-secondary transition-colors duration-fast ease-signal hover:text-pink"
          >
            <Icon name="arrow_back" size={20} />
          </button>
          {/* Wordmark removed — PageShell's bar carries the WorldSwitcher in
              its place, so back and more sit at the two ends. */}
          <button
            aria-label="More"
            onClick={() => setActionsOpen(true)}
            className="text-text-secondary transition-colors duration-fast ease-signal hover:text-pink"
          >
            <Icon name="more_horiz" size={20} />
          </button>
        </>
      }
    >
      {/* MAGAZINE SPREAD — fixed viewport on desktop, stacked on mobile.
          Left page: gallery (thumb strip + clickable hero).
          Right page: meta column scrolls independently. */}
      <div className="lg:flex lg:h-[calc(100dvh-4rem)] lg:overflow-hidden">
        {/* LEFT PAGE — gallery */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="relative bg-night lg:flex-1 lg:h-full"
        >
          {/* Inner: mobile = single column hero + horizontal thumbs.
              Desktop = vertical thumbs (left) + large hero (right). */}
          <div className="relative flex h-[78dvh] flex-col lg:h-full lg:flex-row lg:gap-2 lg:p-3">
            {/* THUMB STRIP — desktop vertical (order-first on lg) */}
            <div
              className={cn(
                'hidden lg:flex lg:order-first lg:w-[72px] lg:shrink-0 lg:flex-col lg:gap-2',
                'lg:overflow-y-auto',
              )}
            >
              {profile.photos.map((url, i) => (
                <ThumbButton
                  key={i}
                  url={url}
                  scene={profile.scene}
                  seed={profile.sceneSeed + i}
                  active={i === activePhoto}
                  onClick={() => setActivePhoto(i)}
                  index={i + 1}
                />
              ))}
            </div>

            {/* HERO — clickable to advance, stretches to fill */}
            <div className="relative h-full w-full overflow-hidden lg:flex-1">
              <button
                type="button"
                aria-label="Next photo"
                onClick={() =>
                  setActivePhoto((i) => (i + 1) % Math.max(profile.photos.length, 1))
                }
                className="absolute inset-0 z-0 h-full w-full cursor-pointer"
              >
                <EditorialImage
                  scene={profile.scene}
                  seed={profile.sceneSeed + activePhoto}
                  photoUrl={profile.photos[activePhoto] ?? profile.photos[0]}
                  aspect="aspect-auto"
                  className="h-full"
                />
              </button>

              {/* Heavy bottom scrim */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(5,6,8,0.30) 0%, rgba(5,6,8,0) 25%, rgba(5,6,8,0) 55%, rgba(5,6,8,0.92) 100%)',
                }}
              />

              {/* Close X — top-right, available on every viewport */}
              <CloseProfileButton onClose={() => navigate(ROUTES.feed)} />

              {/* Top status row */}
              <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-5 md:p-7">
                <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-secondary">
                  <span className="text-pink">No.</span> {profile.id.split('_')[1]}
                  <span className="ml-3 text-pink">·</span>{' '}
                  <span className="text-text-primary">{profile.location.toUpperCase()}</span>
                </span>
                {profile.presence === 'live' && (
                  <span className="flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                    <StatusDot status="live" size="h-1.5 w-1.5" />
                    live now
                  </span>
                )}
              </div>

              {/* Photo counter pill (only when multiple) */}
              {profile.photos.length > 1 && (
                <span className="pointer-events-none absolute right-4 top-16 z-10 inline-flex items-center gap-1 border border-white/[0.10] bg-ink/85 px-2 py-0.5 font-display text-[9px] font-bold uppercase tracking-widest text-text-secondary backdrop-blur md:right-6 md:top-20">
                  {activePhoto + 1} / {profile.photos.length}
                </span>
              )}

              {/* Bottom-anchored editorial */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 p-6 md:p-10 lg:p-12">
                <SectionMarker className="mb-4">
                  {BAND_LABEL[profile.band]} · {profile.distance}
                </SectionMarker>
                <h1
                  className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-display font-black uppercase tracking-display"
                  style={{
                    fontSize: 'clamp(48px, 7vw, 116px)',
                    lineHeight: 0.86,
                    letterSpacing: '-0.05em',
                  }}
                >
                  <span>
                    {profile.handle}<span className="text-pink">.</span>
                  </span>
                  {profile.isVerified && (
                    <span
                      aria-label="Verified"
                      title="Verified"
                      className="inline-flex items-center justify-center text-pink"
                      style={{ filter: 'drop-shadow(0 0 6px rgba(255,12,182,0.6))' }}
                    >
                      <Icon name="verified" filled size={36} />
                    </span>
                  )}
                </h1>
                <p className="mt-4 max-w-xl text-base text-text-secondary md:text-lg">
                  {profile.mood}
                </p>
              </div>
            </div>

            {/* MOBILE THUMB STRIP — horizontal, below hero */}
            <div
              className={cn(
                'flex shrink-0 gap-1.5 overflow-x-auto bg-ink/60 p-2 backdrop-blur lg:hidden',
              )}
            >
              {profile.photos.map((url, i) => (
                <ThumbButton
                  key={i}
                  url={url}
                  scene={profile.scene}
                  seed={profile.sceneSeed + i}
                  active={i === activePhoto}
                  onClick={() => setActivePhoto(i)}
                  index={i + 1}
                  orientation="horizontal"
                />
              ))}
            </div>
          </div>
        </motion.section>

        {/* RIGHT PAGE — meta column. Scrolls independently on desktop. */}
        <motion.aside
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
          className={cn(
            'relative bg-ink',
            'lg:w-[460px] lg:shrink-0 lg:h-full lg:overflow-y-auto',
            'lg:border-l lg:border-white/[0.07]',
            'px-5 py-7 md:px-10 md:py-10',
            'lg:px-9 lg:py-10',
          )}
        >
          {/* Tonight block */}
          <section className="mb-9">
            <SectionMarker className="mb-4">Tonight</SectionMarker>
            <p className="font-display text-2xl font-bold uppercase leading-tight tracking-tight md:text-3xl">
              {profile.mood}<span className="text-pink">.</span>
            </p>
            <p className="mt-4 font-display text-[10px] font-bold uppercase italic tracking-widest text-text-muted">
              What you do with it is your business<span className="text-pink">.</span>
            </p>
          </section>

          {/* About block */}
          <section className="mb-9">
            <SectionMarker className="mb-4" muted>
              About
            </SectionMarker>
            <p className="text-sm leading-relaxed text-text-secondary md:text-base">
              {profile.bio}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {profile.interests.map((tag) => (
                <SignalPill key={tag}>{tag}</SignalPill>
              ))}
            </div>
            {profile.isVerified && (
              <p className="mt-4 inline-flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                <Icon name="verified" filled size={14} />
                Verified handle
              </p>
            )}
          </section>

          {/*
            Grouped metadata.

            This rail used to run tribe / relationship / position / status /
            last-tested / pulse / distance down one continuous column with a
            hairline under every row, which meant users had to decode it. The
            data is unchanged; it is now sorted into named groups — the same
            grouping `.men` gets — with space instead of rules between rows.

            Pink also came off Position, Cock and Pulse here. Those were pink
            because they felt important, not because they were states, and the
            accumulated magenta was drowning the actual primary action.
          */}
          <MetaStack className="mb-9">
            <MetaGroup title="Stats">
              <MetaRow label="Height" value={profile.height} />
              <MetaRow label="Weight" value={profile.weight} />
              <MetaRow label="Body" value={profile.body} />
              <MetaRow label="Position" value={profile.position} />
              {profile.cockSize && <MetaRow label="Cock" value={profile.cockSize} />}
              {profile.tribe && <MetaRow label="Tribe" value={profile.tribe} />}
              <MetaRow label="Relationship" value={profile.relationship} />
              {profile.age && <MetaRow label="Age" value={String(profile.age)} />}
            </MetaGroup>

            <MetaGroup title="Intent">
              <div className="flex flex-wrap gap-2">
                {profile.lookingFor.map((tag) => (
                  <SignalPill key={tag} band="warm">
                    {tag}
                  </SignalPill>
                ))}
              </div>
              <MetaRow label="Status" value={profile.status} />
            </MetaGroup>

            {profile.lastTested && (
              <MetaGroup title="Health">
                <MetaRow
                  label="Last tested"
                  value={new Date(profile.lastTested).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                />
                <p className="font-display text-[10px] font-bold uppercase italic tracking-widest text-text-muted">
                  Stay smart<span className="text-pink">.</span> Stay tested
                  <span className="text-pink">.</span>
                </p>
              </MetaGroup>
            )}

            <MetaGroup title="Location">
              <MetaRow label="Location" value={profile.location} />
              <MetaRow label="Distance" value={profile.distance} />
              <MetaRow label="Pulse" value={BAND_LABEL[profile.band]} />
              <MetaRow
                label="Presence"
                value={
                  <span className="inline-flex items-center gap-2">
                    {profile.presence}
                    <StatusDot status={profile.presence} size="h-1.5 w-1.5" />
                  </span>
                }
              />
            </MetaGroup>
          </MetaStack>

          {/*
            Action hierarchy, borrowed from `.men` — which was the clearest
            thing on either site. "Send a Signal" kept its prominence, but the
            supporting actions are now visible instead of nearly invisible:
            primary → secondary → save.
          */}
          <ActionBar
            primary={
              <KitButton
                size="lg"
                fullWidth
                disabled={messaging}
                leading={
                  <Icon
                    name={
                      profile.ownerId && profile.ownerId !== user?.id
                        ? 'chat_bubble'
                        : 'bolt'
                    }
                    size={18}
                  />
                }
                onClick={() => {
                  capture('message_start', 'profile', profile.id);
                  if (profile.ownerId && profile.ownerId !== user?.id) handleMessage();
                }}
              >
                {messaging
                  ? 'Opening…'
                  : profile.ownerId && profile.ownerId !== user?.id
                    ? 'Message'
                    : 'Send a signal'}
              </KitButton>
            }
            save={<SaveControl saved={favourited} onToggle={setFavourited} />}
            secondary={
              profile.ownerId && profile.ownerId !== user?.id ? undefined : (
                <KitButton
                  variant="secondary"
                  size="md"
                  fullWidth
                  leading={<Icon name="chat_bubble" size={16} />}
                  onClick={() => {
                    capture('message_start', 'profile', profile.id);
                    handleMessage();
                  }}
                >
                  Message
                </KitButton>
              )
            }
          />
          <p className="mt-3 text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
            One message<span className="text-pink">.</span> No openers.
          </p>

          {/* ADJACENCY — lives inside the right page so it scrolls with the rest. */}
          <section className="mt-12 border-t border-white/[0.06] pt-10">
            <header className="mb-5 flex items-center gap-4">
              <SectionMarker>Nearby · {profile.location}</SectionMarker>
              <span aria-hidden className="h-px flex-1 bg-white/10" />
              <Link
                to={ROUTES.feed}
                className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted hover:text-pink"
              >
                See all →
              </Link>
            </header>
            <div className="grid grid-cols-3 gap-1">
              {others.map((p, i) => (
                <GridTile key={p.id} profile={p} index={i} />
              ))}
            </div>

            {elsewhere.length > 0 && (
              <>
                <header className="mb-5 mt-10 flex items-center gap-4">
                  <SectionMarker muted>Elsewhere tonight</SectionMarker>
                  <span aria-hidden className="h-px flex-1 bg-white/10" />
                </header>
                <div className="grid grid-cols-3 gap-1">
                  {elsewhere.map((p, i) => (
                    <GridTile key={p.id} profile={p} index={i} />
                  ))}
                </div>
              </>
            )}
          </section>
        </motion.aside>
      </div>

      {/* Profile actions sheet */}
      <ActionSheet
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        eyebrow="Profile actions"
        title={
          <>
            @{profile.handle}<span className="text-pink">.</span>
          </>
        }
        items={buildProfileActions({
          favourited,
          blocked: profile.ownerId ? isBlocked(profile.ownerId) : false,
          canBlockOrReport: Boolean(profile.ownerId && profile.ownerId !== user?.id),
          onFavourite: () => {
            setFavourited((v) => !v);
            setToast(favourited ? 'Removed from favourites.' : 'Added to favourites.');
          },
          onShare: () => setToast('Share link copied.'),
          onBlock: async () => {
            if (!profile.ownerId) return;
            const already = isBlocked(profile.ownerId);
            const result = already
              ? await unblock(profile.ownerId)
              : await block(profile.ownerId);
            if (!result.ok) {
              setToast(result.error ?? 'Could not update block.');
              return;
            }
            setToast(already ? `Unblocked @${profile.handle}.` : `Blocked @${profile.handle}.`);
            if (!already) {
              // Slight delay so the toast registers before we leave the page.
              setTimeout(() => navigate(ROUTES.feed), 600);
            }
          },
          onReport: () => {
            setActionsOpen(false);
            setReportOpen(true);
          },
        })}
      />

      {/* Report sheet — only relevant for real profiles (with an ownerId). */}
      <ReportSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        reportedUserId={profile.ownerId ?? null}
        reportedHandle={profile.handle}
        onSuccess={() => setToast(`Reported @${profile.handle}. A human reviews within 24 hours.`)}
      />

      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className={cn(
            'fixed inset-x-0 z-30 mx-auto flex max-w-md justify-center px-5',
            // Sit above the Orb on mobile, just above the bottom edge on desktop
            'bottom-[calc(96px+env(safe-area-inset-bottom))] md:bottom-12',
          )}
          aria-live="polite"
          role="status"
        >
          <p
            className={cn(
              'border border-pink/40 bg-ink/95 px-5 py-3 text-center font-display text-[10px] font-bold uppercase tracking-widest text-text-primary shadow-glow-soft backdrop-blur',
            )}
          >
            <span className="text-pink">— </span>
            {toast}
          </p>
        </motion.div>
      )}
    </PageShell>
  );
};

const buildProfileActions = ({
  favourited,
  blocked,
  canBlockOrReport,
  onFavourite,
  onShare,
  onBlock,
  onReport,
}: {
  favourited: boolean;
  blocked: boolean;
  canBlockOrReport: boolean;
  onFavourite: () => void;
  onShare: () => void;
  onBlock: () => void;
  onReport: () => void;
}): ActionSheetItem[] => {
  const items: ActionSheetItem[] = [
    {
      id: 'favourite',
      label: favourited ? 'Remove from favourites' : 'Save to favourites',
      sub: 'Pinned to the top of your feed.',
      icon: favourited ? 'star' : 'star_outline',
      onSelect: onFavourite,
    },
    {
      id: 'share',
      label: 'Share profile',
      sub: 'Copy a link only people in the room can open.',
      icon: 'share',
      onSelect: onShare,
    },
  ];
  if (canBlockOrReport) {
    items.push({
      id: 'block',
      label: blocked ? 'Unblock' : 'Block',
      sub: blocked
        ? 'They appear in your feed again.'
        : 'They cannot see you. You cannot see them.',
      icon: 'block',
      destructive: !blocked,
      onSelect: onBlock,
    });
    items.push({
      id: 'report',
      label: 'Report',
      sub: 'A human reviews this within 24 hours.',
      icon: 'flag',
      destructive: true,
      onSelect: onReport,
    });
  }
  return items;
};

const ThumbButton = ({
  url,
  scene,
  seed,
  active,
  onClick,
  index,
  orientation = 'vertical',
}: {
  url: string;
  scene: React.ComponentProps<typeof EditorialImage>['scene'];
  seed: number;
  active: boolean;
  onClick: () => void;
  index: number;
  orientation?: 'vertical' | 'horizontal';
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Photo ${index}`}
    aria-pressed={active}
    className={cn(
      'relative shrink-0 overflow-hidden border transition-[border-color] duration-fast ease-signal',
      orientation === 'vertical' ? 'aspect-square w-full' : 'h-14 w-14',
      active
        ? 'border-pink shadow-glow-soft'
        : 'border-white/[0.10] hover:border-pink/40',
    )}
  >
    <EditorialImage
      scene={scene}
      seed={seed}
      photoUrl={url}
      aspect="aspect-square"
    />
    <span
      className={cn(
        'absolute left-1 top-1 font-display text-[8px] font-bold uppercase tracking-widest',
        active ? 'text-pink' : 'text-text-muted',
      )}
    >
      {String(index).padStart(2, '0')}
    </span>
  </button>
);

// SignalRow retired — the kit's shared `MetaRow` replaced it, so this rail and
// HOTTR.men's render metadata with identical rhythm, spacing and figures.
